import { gql } from '@apollo/client';
import { DocumentNode } from 'graphql';
import { combineLatest, map, merge, Observable, of, partition, Subject, tap } from 'rxjs';
import { GridHelpers } from './helpers/grid';
import { InstallationRate, QuoteData } from './quote-data';
import { isNil } from 'lodash-es';
import { QuotingApollo } from './apollo';

export const getInstallationRates: DocumentNode = gql`
  query installationRates {
    installationRates(orderBy: area_ASC) {
      id
      installationRateType {
        id
        description
        costPerWeek
      }
      days
      area
      netting
    }
  }
`;

export class InstallationService {
  calculated$: Observable<boolean>;
  private calculated = new Subject<boolean>();

  constructor(private apollo: QuotingApollo) {
    this.calculated$ = this.calculated.asObservable();
  }

  calculate(quoteData: QuoteData): Observable<QuoteData> {
    const [install, noinstall] = partition(
      of(quoteData),
      (qd: QuoteData) => qd.GeneralData.CostInstallation,
    );
    return merge(
      install.pipe(
        tap(() => this.calculated.next(false)),
        (val) =>
          combineLatest([
            val,
            this.apollo.watchQuery<any>({ query: getInstallationRates }).valueChanges,
          ]),
        map(([newQd, { data }]) => {
          const installRates = data.installationRates as InstallationRate[];
          this.chooseInstaller(newQd, installRates);
          this.calculated.next(true);
          return newQd;
        }),
      ),
      noinstall,
    );
  }

  chooseInstaller(quoteData: QuoteData, installationRates: InstallationRate[]): void {
    // if legacy installation data, don't recalculate
    if (quoteData.InstallationData.Locked) {
      return;
    }

    const area = GridHelpers.calculateRoundedArea(quoteData, 0);

    // Automatically select Large Team Size for floors exceeding 1500 square metres
    if (area > 1500) {
      quoteData.InstallationData.TeamSize = 'Large';
    }
    const installationRate = installationRates.find(
      (installationRate) =>
        installationRate.area > area &&
        installationRate.installationRateType.description ===
          (quoteData.InstallationData.ClearBuildJob ? 'Normal' : 'Genie'),
    );

    if (
      (quoteData.InstallationData.IsInstallationAuto ||
        quoteData.InstallationData.SystemDisabledAutoInstallation) &&
      installationRate
    ) {
      if (quoteData.InstallationData.TeamSize === 'Normal') {
        quoteData.InstallationData = {
          IsInstallationAuto: true,
          ClearBuildJob: quoteData.InstallationData.ClearBuildJob,
          Area: area,
          TeamSize: quoteData.InstallationData.TeamSize,
          NormalTeamCost: quoteData.InstallationData.NormalTeamCost,
          LargeTeamCost: quoteData.InstallationData.LargeTeamCost,
          Days: installationRate.days,
          Cost: quoteData.InstallationData.NormalTeamCost,
          Netting: installationRate.netting,
          TotalCost:
            installationRate.days * quoteData.InstallationData.NormalTeamCost +
            area * installationRate.netting,
          Stairs: installationRate.days,
          Handrail: installationRate.days * 10,
          Locked: false,
          SystemDisabledAutoInstallation: false,
        };
      } else {
        const days = Math.ceil(area / installationRate.installationRateType.costPerWeek) * 5;
        quoteData.InstallationData = {
          IsInstallationAuto: true,
          ClearBuildJob: quoteData.InstallationData.ClearBuildJob,
          Area: area,
          TeamSize: quoteData.InstallationData.TeamSize,
          NormalTeamCost: quoteData.InstallationData.NormalTeamCost,
          LargeTeamCost: quoteData.InstallationData.LargeTeamCost,
          Days: days,
          Cost: quoteData.InstallationData.LargeTeamCost,
          Netting: installationRate.netting,
          TotalCost:
            area * quoteData.InstallationData.LargeTeamCost + area * installationRate.netting,
          Stairs: days,
          Handrail: days * 10,
          Locked: false,
          SystemDisabledAutoInstallation: false,
        };
      }
    } else {
      quoteData.InstallationData = {
        IsInstallationAuto: false,
        ClearBuildJob: quoteData.InstallationData.ClearBuildJob,
        Area: area,
        TeamSize: quoteData.InstallationData.TeamSize,
        NormalTeamCost: quoteData.InstallationData.NormalTeamCost,
        LargeTeamCost: quoteData.InstallationData.LargeTeamCost,
        Days: quoteData.InstallationData.Days,
        Cost: quoteData.InstallationData.Cost,
        Netting: quoteData.InstallationData.Netting,
        TotalCost: quoteData.InstallationData.TotalCost,
        Stairs: quoteData.InstallationData.Days,
        Handrail: quoteData.InstallationData.Days * 10,
        Locked: false,
        SystemDisabledAutoInstallation: isNil(installationRate),
      };
    }
  }
}
